import { useCallback } from 'react';
import { ChatMessage, UploadedFile } from '../types';

interface UseMessageSendingProps {
    wsUiState: any;
    setWsUiState: React.Dispatch<React.SetStateAction<any>>;
    localMessages: ChatMessage[];
    setLocalMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
    allQuestions: any[];
    currentQuestionIndex: number;
    handleQuestionAnswer: (questionId: string, answer: string) => void;
    sendWebSocketControlMessage: (action: string, data: any) => void;
    generateTemplate: (feedback?: string) => Promise<void>;
    createJob: (jobName: string) => Promise<void>;
    handleFileUploadAndProcess: (file: File) => Promise<void>;
    onPreviewFile?: (file: UploadedFile) => void;
}

/**
 * Hook for handling message sending logic
 */
export const useMessageSending = ({
    wsUiState,
    setWsUiState,
    localMessages,
    setLocalMessages,
    allQuestions,
    currentQuestionIndex,
    handleQuestionAnswer,
    sendWebSocketControlMessage,
    generateTemplate,
    createJob,
    handleFileUploadAndProcess,
    onPreviewFile
}: UseMessageSendingProps) => {
    
    // Handle sending message in WebSocket control mode
    const handleSendMessage = useCallback(async ({ 
        detail: { value } 
    }: { 
        detail: { value: string } 
    }, files: File[] = []) => {
        const hasText = value?.trim();
        const hasFiles = files.length > 0;
        
        if (!hasText && !hasFiles) return;
        
        // Check if we're awaiting job name
        if (wsUiState.awaiting_job_name && hasText) {
            // User provided job name, create the job
            const jobName = hasText;
            
            // Add user message
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `user-job-name-${Date.now()}`,
                type: 'user',
                content: jobName,
                timestamp: new Date(),
                status: 'sent'
            }]);
            
            // Create the job
            await createJob(jobName);
            
            // Clear the awaiting flag
            setWsUiState((prev: any) => ({
                ...prev,
                awaiting_job_name: false,
                input_enabled: false
            }));
            
            return { shouldClearInput: true, shouldClearFiles: true };
        }
        
        // Check if we're awaiting feedback for validation errors
        if (wsUiState.awaiting_feedback && hasText) {
            const feedback = hasText;
            
            // Add user message
            setLocalMessages((prev: ChatMessage[]) => [...prev, {
                id: `user-feedback-${Date.now()}`,
                type: 'user',
                content: feedback,
                timestamp: new Date(),
                status: 'sent'
            }]);
            
            // Retry generation with feedback
            await generateTemplate(feedback);
            
            // Clear the awaiting flag
            setWsUiState((prev: any) => ({
                ...prev,
                awaiting_feedback: false,
                input_enabled: false
            }));
            
            return { shouldClearInput: true, shouldClearFiles: true };
        }
        
        // Handle file upload confirmation
        if (hasFiles && wsUiState.show_file_upload) {
            const file = files[0];
            
            // Store file reference globally for later upload
            window.__ADDAPT_PENDING_FILE__ = file;
            
            // Show file selected message with confirmation buttons
            const confirmMessage: ChatMessage = {
                id: `file-selected-${Date.now()}`,
                type: 'assistant',
                content: `📎 **File Selected:** ${file.name}\n\nSize: ${(file.size / 1024).toFixed(2)} KB\n\nWould you like to upload this file or select a different one?`,
                timestamp: new Date(),
                status: 'sent',
                supportPrompts: [
                    {
                        id: 'preview-file',
                        text: '👁️ Preview File'
                    },
                    {
                        id: 'upload-file',
                        text: '✅ Upload This File'
                    },
                    {
                        id: 'change-file',
                        text: '🔄 Change File'
                    }
                ]
            };
            setLocalMessages((prev: ChatMessage[]) => [...prev, confirmMessage]);
            
            // Hide file input button and disable text input
            setWsUiState((prev: any) => ({
                ...prev,
                show_file_upload: false, // Hide file input button
                input_enabled: false,
                input_placeholder: 'Please select an action from the buttons above...'
            }));
            
            return { shouldClearInput: true, shouldClearFiles: true }; // Clear files from input
        }
        
        // Handle text input
        if (hasText) {
            const lastQuestion = [...localMessages].reverse().find((m: ChatMessage) => 
                m.type === 'assistant' && m.id.startsWith('question-')
            );
            
            // Check if we're waiting for a job name
            const waitingForJobName = wsUiState.input_placeholder?.includes('job name') && 
                                     window.__ADDAPT_TEMPLATE__?.templateId;
            
            // Check if we're waiting for feedback
            const waitingForFeedback = wsUiState.input_placeholder?.includes('feedback') && 
                                      window.__ADDAPT_TEMPLATE__?.templateId;
            
            // Check if this is a question answer that handleQuestionAnswer will handle
            const isQuestionAnswer = lastQuestion && wsUiState.input_enabled && 
                                    allQuestions.length > 0 && currentQuestionIndex < allQuestions.length;
            
            // Only add user message if handleQuestionAnswer won't do it
            if (!isQuestionAnswer) {
                const userMessage: ChatMessage = {
                    id: `user-${Date.now()}`,
                    type: 'user',
                    content: value.trim(),
                    timestamp: new Date(),
                    status: 'sending'
                };
                setLocalMessages((prev: ChatMessage[]) => [...prev, userMessage]);
            }
            
            if (waitingForJobName) {
                // Create job with the provided name
                await createJob(value.trim());
            } else if (waitingForFeedback) {
                // Submit feedback and regenerate
                const templateId = window.__ADDAPT_TEMPLATE__?.templateId;
                sendWebSocketControlMessage('submit_feedback', {
                    templateId,
                    feedback: value.trim(),
                    jobId: window.__ADDAPT_TEMPLATE__?.jobId
                });
            } else if (lastQuestion && wsUiState.input_enabled) {
                // Handle question answer in sequential flow
                const questionId = lastQuestion.id.replace('question-', '');
                
                // If we have allQuestions (new protocol), use the sequential handler
                if (allQuestions.length > 0 && currentQuestionIndex < allQuestions.length) {
                    const currentQuestion = allQuestions[currentQuestionIndex];
                    handleQuestionAnswer(currentQuestion.id, value.trim());
                } else {
                    // Fallback to old protocol
                    sendWebSocketControlMessage('answer_question', {
                        question_id: questionId,
                        answer: value.trim()
                    });
                }
                
                // Hide file upload if it was showing (backend will re-enable if needed)
                if (wsUiState.show_file_upload) {
                    setWsUiState((prev: any) => ({
                        ...prev,
                        show_file_upload: false
                    }));
                }
            } else {
                sendWebSocketControlMessage('message', {
                    message: value.trim()
                });
            }
        }
        
        return { shouldClearInput: true, shouldClearFiles: true };
    }, [
        wsUiState,
        localMessages,
        allQuestions,
        currentQuestionIndex,
        setLocalMessages,
        setWsUiState,
        createJob,
        generateTemplate,
        handleQuestionAnswer,
        sendWebSocketControlMessage,
        handleFileUploadAndProcess,
        onPreviewFile
    ]);

    return {
        handleSendMessage
    };
};

